import { useEffect } from 'react';

/**
 * Registers are the reason this application exists, and on a phone they are the one
 * thing a plain stylesheet cannot rescue: a trial balance is eight columns wide, so a
 * 390px screen can only offer sideways scrolling, which hides the very columns the
 * reader came for.
 *
 * Below the shell breakpoint the stylesheet re-flows every table into one card per
 * row, with each value carrying its own column heading. CSS cannot read a `<th>` from
 * inside a `<td>`, so the headings are mirrored onto the body cells here as
 * `data-label` and drawn by `td::before`. Marking the table `data-stack="on"` is what
 * arms the stylesheet, which keeps the card layout and the labels in lockstep.
 *
 * A table opts out with `data-stack="off"` when its rows are already narrow enough to
 * read as a row.
 */

const stackedTableSelector = 'table:not([data-stack="off"])';

/** Header text for one cell, ignoring the visually-hidden captions used on action columns. */
function headingText(cell: HTMLTableCellElement): string {
  return (cell.textContent ?? '').replace(/\s+/gu, ' ').trim();
}

/**
 * Resolves one heading per rendered column, honouring `rowspan` and `colspan` so a
 * grouped header (`Opening` over `Debit`/`Credit`) reaches its leaf cells as
 * `Opening · Debit` rather than sliding every label one column across.
 */
function columnHeadings(table: HTMLTableElement): readonly string[] {
  const head = table.tHead;
  if (head === null) return [];

  const grid: string[][] = [];
  for (let rowIndex = 0; rowIndex < head.rows.length; rowIndex += 1) {
    const cells = head.rows[rowIndex]!.cells;
    let column = 0;
    for (let cellIndex = 0; cellIndex < cells.length; cellIndex += 1) {
      const cell = cells[cellIndex]!;
      while (grid[rowIndex]?.[column] !== undefined) column += 1;
      const text = headingText(cell);
      for (let spannedRow = 0; spannedRow < Math.max(cell.rowSpan, 1); spannedRow += 1) {
        for (let spannedColumn = 0; spannedColumn < Math.max(cell.colSpan, 1); spannedColumn += 1) {
          const row = (grid[rowIndex + spannedRow] ??= []);
          row[column + spannedColumn] = text;
        }
      }
      column += Math.max(cell.colSpan, 1);
    }
  }

  const width = grid.reduce((widest, row) => Math.max(widest, row.length), 0);
  const headings: string[] = [];
  for (let column = 0; column < width; column += 1) {
    const parts: string[] = [];
    for (let rowIndex = 0; rowIndex < grid.length; rowIndex += 1) {
      const text = grid[rowIndex]?.[column];
      if (text !== undefined && text.length > 0 && text !== parts.at(-1)) parts.push(text);
    }
    headings[column] = parts.join(' · ');
  }
  return headings;
}

/** Writes an attribute only when it would change, so React's nodes are left alone otherwise. */
function setAttribute(element: Element, name: string, value: string): void {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
}

function labelBodyCells(table: HTMLTableElement, headings: readonly string[]): void {
  const sections = [...table.tBodies, ...(table.tFoot === null ? [] : [table.tFoot])];
  for (const section of sections) {
    for (let rowIndex = 0; rowIndex < section.rows.length; rowIndex += 1) {
      const cells = section.rows[rowIndex]!.cells;
      let column = 0;
      for (let cellIndex = 0; cellIndex < cells.length; cellIndex += 1) {
        const cell = cells[cellIndex]!;
        const span = Math.max(cell.colSpan, 1);
        // A spanning cell answers to no single column — it is a caption for the row
        // ("Brought forward", "Totals"), so it stacks full width without a heading.
        if (span > 1) {
          setAttribute(cell, 'data-span', 'true');
          cell.removeAttribute('data-label');
        } else {
          cell.removeAttribute('data-span');
          setAttribute(cell, 'data-label', headings[column] ?? '');
        }
        column += span;
      }
    }
  }
}

/**
 * `display: block` is what makes the cards, and it also strips a table's implicit roles
 * from the accessibility tree — a register would stop being a register for anyone reading
 * it aloud. The roles are therefore stated outright. They are redundant while the table is
 * a table, which is harmless, and load-bearing once it is a stack of cards.
 */
function restoreTableSemantics(table: HTMLTableElement): void {
  setAttribute(table, 'role', 'table');
  for (const group of [table.tHead, ...table.tBodies, table.tFoot]) {
    if (group === null) continue;
    setAttribute(group, 'role', 'rowgroup');
    for (let rowIndex = 0; rowIndex < group.rows.length; rowIndex += 1) {
      const row = group.rows[rowIndex]!;
      setAttribute(row, 'role', 'row');
      for (let cellIndex = 0; cellIndex < row.cells.length; cellIndex += 1) {
        const cell = row.cells[cellIndex]!;
        setAttribute(cell, 'role', cell.tagName === 'TH' ? 'columnheader' : 'cell');
      }
    }
  }
}

function syncTable(table: HTMLTableElement): void {
  const headings = columnHeadings(table);
  if (headings.length === 0) return;
  labelBodyCells(table, headings);
  restoreTableSemantics(table);
  setAttribute(table, 'data-stack', 'on');
}

/** Labels every table currently in the document. Safe to call as often as the DOM changes. */
export function syncStackedTables(root: ParentNode = document): void {
  for (const table of root.querySelectorAll<HTMLTableElement>(stackedTableSelector)) syncTable(table);
}

/**
 * Keeps the labels in step with the DOM for the lifetime of the application. Only
 * child-list mutations are observed, so writing the labels never re-triggers the pass,
 * and the work is coalesced into one frame however many rows React replaced.
 */
export function useStackedTables(): void {
  useEffect(() => {
    if (typeof MutationObserver !== 'function') return undefined;

    let frame = 0;
    const flush = () => {
      frame = 0;
      syncStackedTables();
    };
    const schedule = () => {
      if (frame !== 0) return;
      frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(flush) : window.setTimeout(flush, 0);
    };

    flush();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frame !== 0 && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    };
  }, []);
}
